/**
 * @copyright 2024 Cooltrax Pty Ltd. All rights reserved.
 */
import React from 'react'

import { MdHome, MdErrorOutline } from 'react-icons/md'

const NotFoundPage: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = React.useState(false)

  React.useEffect(() => {
    // Check for theme from HTML data attribute (daisyUI theme)
    const checkTheme = () => {
      const htmlElement = document.documentElement
      const theme = htmlElement.getAttribute('data-theme')
      const isDark =
        theme?.includes('dark') ||
        theme === 'night' ||
        theme === 'halloween' ||
        theme === 'forest' ||
        theme === 'black' ||
        theme === 'luxury' ||
        theme === 'dracula' ||
        theme === 'synthwave' ||
        theme === 'cyberpunk' ||
        theme === 'dim' ||
        theme === 'sunset' ||
        theme === 'abyss'

      // Fallback to system preference if no theme is set
      if (!theme) {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        setIsDarkMode(prefersDark)
      } else {
        setIsDarkMode(isDark || false)
      }
    }

    checkTheme()

    // Listen for theme changes
    const observer = new MutationObserver(checkTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    mediaQuery.addEventListener('change', checkTheme)

    return () => {
      observer.disconnect()
      mediaQuery.removeEventListener('change', checkTheme)
    }
  }, [])

  const handleGoHome = () => {
    window.location.href = '/'
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // Hide the image if it fails to load
    e.currentTarget.style.display = 'none'
  }

  return (
    <div className="min-h-screen bg-base-100 flex items-center justify-center p-4">
      <div className="card bg-base-100 shadow-xl max-w-md w-full">
        <div className="card-body text-center">
          {/* Cooltrax Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={isDarkMode ? '/logo-dark.png' : '/logo-light.png'}
              alt="Cooltrax"
              className="h-12 w-auto"
              onError={handleImageError}
            />
          </div>

          <div className="flex justify-center mb-4">
            <MdErrorOutline className="text-6xl text-error" />
          </div>

          <h1 className="card-title text-2xl text-error justify-center mb-2">Page Not Found</h1>

          <p className="text-base-content/70 mb-6">The page you’re looking for does not exist.</p>

          <div className="card-actions justify-center">
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button onClick={handleGoHome} className="btn btn-outline btn-md">
                <MdHome className="w-4 h-4 mr-2" />
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NotFoundPage
